// C:\Users\aleja\Code\inventory_analytics\src\front\src\store\flux\warehouseCostAnalyticsActions.js

import getApiBaseUrl from "../../utils/api";

const API_URL = getApiBaseUrl();

const buildUrlWithQuery = (path, filters = {}) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      params.append(key, value);
    }
  });

  const queryString = params.toString();
  return `${API_URL}${path}${queryString ? `?${queryString}` : ""}`;
};

const getFilenameFromDisposition = (contentDisposition, fallbackFilename) => {
  if (!contentDisposition) return fallbackFilename;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
  }

  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || fallbackFilename;
};

const downloadBlob = (blob, filename) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const getResponseErrorMessage = async (response, fallbackMessage) => {
  const contentType = response.headers.get("Content-Type") || "";

  if (contentType.includes("application/json")) {
    const payload = await response.json().catch(() => null);
    return payload?.error || payload?.msg || fallbackMessage;
  }

  return fallbackMessage;
};

const warehouseCostAnalyticsActions = ({ getStore, getActions, setStore }) => ({
  getWarehouseCostAnalytics: async (filters = {}) => {
    try {
      const { token } = getStore();
      const url = buildUrlWithQuery("/api/warehouse-cost-analytics", filters);

      setStore({
        warehouseCostAnalyticsLoading: true,
        warehouseCostAnalyticsError: null,
      });

      const response = await fetch(url, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          warehouseCostAnalytics: null,
          warehouseCostAnalyticsLoading: false,
          warehouseCostAnalyticsError: "Request failed",
        });

        return { success: false, data: null, error: "Request failed" };
      }

      const responseData = await response.json();

      if (!responseData.success) {
        const message =
          responseData.error || "Error cargando analítica de coste de almacén";

        setStore({
          warehouseCostAnalytics: null,
          warehouseCostAnalyticsLoading: false,
          warehouseCostAnalyticsError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, data: null, error: message };
      }

      setStore({
        warehouseCostAnalytics: responseData.data || null,
        warehouseCostAnalyticsLoading: false,
        warehouseCostAnalyticsError: null,
      });

      return {
        success: true,
        data: responseData.data || null,
        error: null,
      };
    } catch (error) {
      console.error("Warehouse cost analytics error:", error);

      const message = "Error cargando analítica de coste de almacén";
      setStore({
        warehouseCostAnalytics: null,
        warehouseCostAnalyticsLoading: false,
        warehouseCostAnalyticsError: message,
      });
      getActions().showToast(message, "error");

      return { success: false, data: null, error: message };
    }
  },

  clearWarehouseCostAnalytics: () => {
    setStore({
      warehouseCostAnalytics: null,
      warehouseCostAnalyticsLoading: false,
      warehouseCostAnalyticsError: null,
    });
  },

  exportDashboardInventoryReport: async () => {
    try {
      const { token } = getStore();

      setStore({
        dashboardExportLoading: true,
        dashboardExportError: null,
      });

      const response = await fetch(`${API_URL}/api/reports/export/inventory`, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          dashboardExportLoading: false,
          dashboardExportError: "Request failed",
        });
        return { success: false, error: "Request failed" };
      }

      if (!response.ok) {
        const message = await getResponseErrorMessage(
          response,
          "Error exportando dashboard",
        );

        setStore({
          dashboardExportLoading: false,
          dashboardExportError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, error: message };
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        const message = "La exportación no devolvió un archivo válido";

        setStore({
          dashboardExportLoading: false,
          dashboardExportError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, error: message };
      }

      const filename = getFilenameFromDisposition(
        response.headers.get("Content-Disposition"),
        "inventory_report.xlsx",
      );
      downloadBlob(blob, filename);

      setStore({
        dashboardExportLoading: false,
        dashboardExportError: null,
      });
      getActions().showToast("Exportación del dashboard completada", "success");

      return { success: true, error: null };
    } catch (error) {
      console.error("Dashboard inventory export error:", error);

      const message = "Error exportando dashboard";
      setStore({
        dashboardExportLoading: false,
        dashboardExportError: message,
      });
      getActions().showToast(message, "error");

      return { success: false, error: message };
    }
  },

  getSuppliersCatalog: async (filters = {}) => {
    return getActions().fetchCatalogCollection({
      path: "/api/catalog/suppliers",
      filters,
      loadingKey: "suppliersCatalogLoading",
      errorKey: "suppliersCatalogError",
      dataKey: "suppliersCatalog",
      errorMessage: "Error cargando catálogo de proveedores",
    });
  },

  exportSuppliersCatalogExcel: async (filters = {}) => {
    try {
      const { token } = getStore();
      const url = buildUrlWithQuery("/api/catalog/suppliers/export.xlsx", filters);

      setStore({
        suppliersCatalogExportLoading: true,
        suppliersCatalogExportError: null,
      });

      const response = await fetch(url, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          suppliersCatalogExportLoading: false,
          suppliersCatalogExportError: "Request failed",
        });
        return { success: false, error: "Request failed" };
      }

      if (!response.ok) {
        const message = await getResponseErrorMessage(
          response,
          "Error exportando proveedores",
        );

        setStore({
          suppliersCatalogExportLoading: false,
          suppliersCatalogExportError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, error: message };
      }

      const blob = await response.blob();
      const filename = getFilenameFromDisposition(
        response.headers.get("Content-Disposition"),
        "suppliers_catalog.xlsx",
      );
      downloadBlob(blob, filename);

      setStore({
        suppliersCatalogExportLoading: false,
        suppliersCatalogExportError: null,
      });
      getActions().showToast("Exportación de proveedores completada", "success");

      return { success: true, error: null };
    } catch (error) {
      console.error("Suppliers Excel export error:", error);

      const message = "Error exportando proveedores";
      setStore({
        suppliersCatalogExportLoading: false,
        suppliersCatalogExportError: message,
      });
      getActions().showToast(message, "error");

      return { success: false, error: message };
    }
  },

  clearSuppliersCatalog: () => {
    setStore({
      suppliersCatalog: [],
      suppliersCatalogLoading: false,
      suppliersCatalogError: null,
      suppliersCatalogExportLoading: false,
      suppliersCatalogExportError: null,
    });
  },

  getPartsCatalog: async (filters = {}) => {
    return getActions().fetchCatalogCollection({
      path: "/api/catalog/parts",
      filters,
      loadingKey: "partsCatalogLoading",
      errorKey: "partsCatalogError",
      dataKey: "partsCatalog",
      errorMessage: "Error cargando catálogo de repuestos",
    });
  },

  clearPartsCatalog: () => {
    setStore({
      partsCatalog: [],
      partsCatalogLoading: false,
      partsCatalogError: null,
    });
  },

  getPartDetail: async (materialId, sortConfig = {}) => {
    const normalizedMaterialId = String(materialId || "").trim();

    if (!normalizedMaterialId) {
      const message = "Material inválido";
      setStore({
        partDetail: null,
        partDetailLoading: false,
        partDetailError: message,
      });
      return { success: false, data: null, error: message };
    }

    try {
      const { token } = getStore();

      setStore({
        partDetail: null,
        partDetailLoading: true,
        partDetailError: null,
      });

      const response = await fetch(
        buildUrlWithQuery(
          `/api/catalog/parts/${encodeURIComponent(normalizedMaterialId)}`,
          {
            sort_by: sortConfig.key,
            sort_dir: sortConfig.direction,
          },
        ),
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      if (response.status === 404) {
        const responseData = await response.json().catch(() => null);
        const message = responseData?.error || "Repuesto no encontrado";

        setStore({
          partDetail: null,
          partDetailLoading: false,
          partDetailError: message,
        });

        return { success: false, data: null, error: message };
      }

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          partDetail: null,
          partDetailLoading: false,
          partDetailError: "Request failed",
        });

        return { success: false, data: null, error: "Request failed" };
      }

      const responseData = await response.json();

      if (!responseData.success) {
        const message = responseData.error || "Error cargando detalle de repuesto";

        setStore({
          partDetail: null,
          partDetailLoading: false,
          partDetailError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, data: null, error: message };
      }

      setStore({
        partDetail: responseData.data || null,
        partDetailLoading: false,
        partDetailError: null,
      });

      return {
        success: true,
        data: responseData.data || null,
        error: null,
      };
    } catch (error) {
      console.error("Part detail fetch error:", error);

      const message = "Error cargando detalle de repuesto";
      setStore({
        partDetail: null,
        partDetailLoading: false,
        partDetailError: message,
      });
      getActions().showToast(message, "error");

      return { success: false, data: null, error: message };
    }
  },

  clearPartDetail: () => {
    setStore({
      partDetail: null,
      partDetailLoading: false,
      partDetailError: null,
    });
  },

  getSupplierDetail: async (supplierId, sortConfig = {}) => {
    const normalizedSupplierId = String(supplierId || "").trim();

    if (!normalizedSupplierId) {
      const message = "Proveedor inválido";
      setStore({
        supplierDetail: null,
        supplierDetailLoading: false,
        supplierDetailError: message,
      });
      return { success: false, data: null, error: message };
    }

    try {
      const { token } = getStore();

      setStore({
        supplierDetail: null,
        supplierDetailLoading: true,
        supplierDetailError: null,
      });

      const response = await fetch(
        buildUrlWithQuery(
          `/api/catalog/suppliers/${encodeURIComponent(normalizedSupplierId)}`,
          {
            sort_by: sortConfig.key,
            sort_dir: sortConfig.direction,
          },
        ),
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      if (response.status === 404) {
        const responseData = await response.json().catch(() => null);
        const message = responseData?.error || "Proveedor no encontrado";

        setStore({
          supplierDetail: null,
          supplierDetailLoading: false,
          supplierDetailError: message,
        });

        return { success: false, data: null, error: message };
      }

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          supplierDetail: null,
          supplierDetailLoading: false,
          supplierDetailError: "Request failed",
        });

        return { success: false, data: null, error: "Request failed" };
      }

      const responseData = await response.json();

      if (!responseData.success) {
        const message = responseData.error || "Error cargando detalle de proveedor";

        setStore({
          supplierDetail: null,
          supplierDetailLoading: false,
          supplierDetailError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, data: null, error: message };
      }

      setStore({
        supplierDetail: responseData.data || null,
        supplierDetailLoading: false,
        supplierDetailError: null,
      });

      return {
        success: true,
        data: responseData.data || null,
        error: null,
      };
    } catch (error) {
      console.error("Supplier detail fetch error:", error);

      const message = "Error cargando detalle de proveedor";
      setStore({
        supplierDetail: null,
        supplierDetailLoading: false,
        supplierDetailError: message,
      });
      getActions().showToast(message, "error");

      return { success: false, data: null, error: message };
    }
  },

  clearSupplierDetail: () => {
    setStore({
      supplierDetail: null,
      supplierDetailLoading: false,
      supplierDetailError: null,
    });
  },

  getSupplierDocuments: async (supplierId) => {
    const normalizedSupplierId = String(supplierId || "").trim();

    if (!normalizedSupplierId) {
      const message = "Proveedor inválido";
      setStore({
        supplierDocuments: [],
        supplierDocumentsLoading: false,
        supplierDocumentsError: message,
      });
      return { success: false, data: [], error: message };
    }

    try {
      const { token } = getStore();

      setStore({
        supplierDocumentsLoading: true,
        supplierDocumentsError: null,
      });

      const response = await fetch(
        `${API_URL}/api/catalog/suppliers/${encodeURIComponent(normalizedSupplierId)}/documents`,
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      if (response.status === 404) {
        const responseData = await response.json().catch(() => null);
        const message = responseData?.error || "Proveedor no encontrado";

        setStore({
          supplierDocuments: [],
          supplierDocumentsLoading: false,
          supplierDocumentsError: message,
        });

        return { success: false, data: [], error: message };
      }

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          supplierDocuments: [],
          supplierDocumentsLoading: false,
          supplierDocumentsError: "Request failed",
        });

        return { success: false, data: [], error: "Request failed" };
      }

      const responseData = await response.json();

      if (!responseData.success) {
        const message =
          responseData.error || "Error cargando documentación del proveedor";

        setStore({
          supplierDocuments: [],
          supplierDocumentsLoading: false,
          supplierDocumentsError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, data: [], error: message };
      }

      const documents = Array.isArray(responseData.data) ? responseData.data : [];
      setStore({
        supplierDocuments: documents,
        supplierDocumentsLoading: false,
        supplierDocumentsError: null,
      });

      return { success: true, data: documents, error: null };
    } catch (error) {
      console.error("Supplier documents fetch error:", error);

      const message = "Error cargando documentación del proveedor";
      setStore({
        supplierDocuments: [],
        supplierDocumentsLoading: false,
        supplierDocumentsError: message,
      });
      getActions().showToast(message, "error");

      return { success: false, data: [], error: message };
    }
  },

  uploadSupplierDocument: async (supplierId, { file, documentType } = {}) => {
    const normalizedSupplierId = String(supplierId || "").trim();

    if (!normalizedSupplierId || !file) {
      const message = !file ? "Selecciona un archivo" : "Proveedor inválido";
      setStore({
        supplierDocumentUploadLoading: false,
        supplierDocumentUploadError: message,
      });
      return { success: false, data: null, error: message };
    }

    try {
      const { token } = getStore();
      const formData = new FormData();
      formData.append("file", file);

      if (String(documentType || "").trim()) {
        formData.append("document_type", String(documentType).trim());
      }

      setStore({
        supplierDocumentUploadLoading: true,
        supplierDocumentUploadError: null,
      });

      const response = await fetch(
        `${API_URL}/api/catalog/suppliers/${encodeURIComponent(normalizedSupplierId)}/documents`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
          },
          body: formData,
        },
      );

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          supplierDocumentUploadLoading: false,
          supplierDocumentUploadError: "Request failed",
        });
        return { success: false, data: null, error: "Request failed" };
      }

      const responseData = await response.json().catch(() => null);

      if (!response.ok || !responseData?.success) {
        const message =
          responseData?.error ||
          responseData?.msg ||
          "Error subiendo documento del proveedor";

        setStore({
          supplierDocumentUploadLoading: false,
          supplierDocumentUploadError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, data: null, error: message };
      }

      setStore({
        supplierDocumentUploadLoading: false,
        supplierDocumentUploadError: null,
      });
      getActions().showToast("Documento subido correctamente", "success");

      return { success: true, data: responseData.data || null, error: null };
    } catch (error) {
      console.error("Supplier document upload error:", error);

      const message = "Error subiendo documento del proveedor";
      setStore({
        supplierDocumentUploadLoading: false,
        supplierDocumentUploadError: message,
      });
      getActions().showToast(message, "error");

      return { success: false, data: null, error: message };
    }
  },

  downloadSupplierDocument: async (supplierId, documentId) => {
    const normalizedSupplierId = String(supplierId || "").trim();

    if (!normalizedSupplierId || !documentId) {
      const message = "Documento inválido";
      setStore({ supplierDocumentActionError: message });
      return { success: false, error: message };
    }

    try {
      const { token } = getStore();

      setStore({
        supplierDocumentDownloadingId: documentId,
        supplierDocumentActionError: null,
      });

      const response = await fetch(
        `${API_URL}/api/catalog/suppliers/${encodeURIComponent(normalizedSupplierId)}/documents/${documentId}/download`,
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          supplierDocumentDownloadingId: null,
          supplierDocumentActionError: "Request failed",
        });
        return { success: false, error: "Request failed" };
      }

      if (!response.ok) {
        const message = await getResponseErrorMessage(
          response,
          "Error descargando documento",
        );

        setStore({
          supplierDocumentDownloadingId: null,
          supplierDocumentActionError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, error: message };
      }

      const blob = await response.blob();
      const filename = getFilenameFromDisposition(
        response.headers.get("Content-Disposition"),
        "supplier_document",
      );
      downloadBlob(blob, filename);

      setStore({
        supplierDocumentDownloadingId: null,
        supplierDocumentActionError: null,
      });

      return { success: true, error: null };
    } catch (error) {
      console.error("Supplier document download error:", error);

      const message = "Error descargando documento";
      setStore({
        supplierDocumentDownloadingId: null,
        supplierDocumentActionError: message,
      });
      getActions().showToast(message, "error");

      return { success: false, error: message };
    }
  },

  previewSupplierDocument: async (supplierId, documentId, fallbackContentType = "") => {
    const normalizedSupplierId = String(supplierId || "").trim();

    if (!normalizedSupplierId || !documentId) {
      const message = "Documento inválido";
      setStore({ supplierDocumentActionError: message });
      return { success: false, data: null, error: message };
    }

    try {
      const { token } = getStore();
      const response = await fetch(
        `${API_URL}/api/catalog/suppliers/${encodeURIComponent(normalizedSupplierId)}/documents/${documentId}/preview`,
        {
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({ supplierDocumentActionError: "Request failed" });
        return { success: false, data: null, error: "Request failed" };
      }

      if (!response.ok) {
        const fallbackMessage =
          response.status === 415
            ? "Vista previa no disponible para este tipo de archivo. Descargue el documento."
            : "Error cargando vista previa";
        const message = await getResponseErrorMessage(response, fallbackMessage);

        setStore({ supplierDocumentActionError: message });
        getActions().showToast(message, "error");

        return { success: false, data: null, error: message };
      }

      const responseBlob = await response.blob();
      const responseContentType =
        responseBlob.type || response.headers.get("Content-Type") || "";
      const contentType =
        responseContentType === "application/octet-stream"
          ? fallbackContentType
          : responseContentType || fallbackContentType;
      const blob =
        contentType && contentType !== responseBlob.type
          ? new Blob([responseBlob], { type: contentType })
          : responseBlob;
      const objectUrl = window.URL.createObjectURL(blob);
      const filename = getFilenameFromDisposition(
        response.headers.get("Content-Disposition"),
        "supplier_document",
      );

      setStore({ supplierDocumentActionError: null });

      return {
        success: true,
        data: {
          objectUrl,
          contentType,
          filename,
        },
        error: null,
      };
    } catch (error) {
      console.error("Supplier document preview error:", error);

      const message = "Error cargando vista previa";
      setStore({ supplierDocumentActionError: message });
      getActions().showToast(message, "error");

      return { success: false, data: null, error: message };
    }
  },

  deleteSupplierDocument: async (supplierId, documentId) => {
    const normalizedSupplierId = String(supplierId || "").trim();

    if (!normalizedSupplierId || !documentId) {
      const message = "Documento inválido";
      setStore({ supplierDocumentActionError: message });
      return { success: false, data: null, error: message };
    }

    try {
      const { token } = getStore();

      setStore({
        supplierDocumentDeletingId: documentId,
        supplierDocumentActionError: null,
      });

      const response = await fetch(
        `${API_URL}/api/catalog/suppliers/${encodeURIComponent(normalizedSupplierId)}/documents/${documentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          supplierDocumentDeletingId: null,
          supplierDocumentActionError: "Request failed",
        });
        return { success: false, data: null, error: "Request failed" };
      }

      const responseData = await response.json().catch(() => null);

      if (!response.ok || !responseData?.success) {
        const message =
          responseData?.error ||
          responseData?.msg ||
          "Error eliminando documento";

        setStore({
          supplierDocumentDeletingId: null,
          supplierDocumentActionError: message,
        });
        getActions().showToast(message, "error");

        return { success: false, data: null, error: message };
      }

      setStore({
        supplierDocumentDeletingId: null,
        supplierDocumentActionError: null,
      });
      getActions().showToast("Documento eliminado", "success");

      return { success: true, data: responseData.data || null, error: null };
    } catch (error) {
      console.error("Supplier document delete error:", error);

      const message = "Error eliminando documento";
      setStore({
        supplierDocumentDeletingId: null,
        supplierDocumentActionError: message,
      });
      getActions().showToast(message, "error");

      return { success: false, data: null, error: message };
    }
  },

  clearSupplierDocuments: () => {
    setStore({
      supplierDocuments: [],
      supplierDocumentsLoading: false,
      supplierDocumentsError: null,
      supplierDocumentUploadLoading: false,
      supplierDocumentUploadError: null,
      supplierDocumentDownloadingId: null,
      supplierDocumentDeletingId: null,
      supplierDocumentActionError: null,
    });
  },

  fetchCatalogCollection: async ({
    path,
    filters = {},
    loadingKey,
    errorKey,
    dataKey,
    errorMessage,
  }) => {
    try {
      const { token } = getStore();
      const url = buildUrlWithQuery(path, filters);

      setStore({
        [loadingKey]: true,
        [errorKey]: null,
      });

      const response = await fetch(url, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      const error = await getActions().handleApiError(response);
      if (error) {
        setStore({
          [dataKey]: [],
          [loadingKey]: false,
          [errorKey]: "Request failed",
        });

        return { success: false, data: [], error: "Request failed" };
      }

      const responseData = await response.json();

      if (!responseData.success) {
        const message = responseData.error || errorMessage;

        setStore({
          [dataKey]: [],
          [loadingKey]: false,
          [errorKey]: message,
        });
        getActions().showToast(message, "error");

        return { success: false, data: [], error: message };
      }

      const rows = Array.isArray(responseData.data) ? responseData.data : [];
      setStore({
        [dataKey]: rows,
        [loadingKey]: false,
        [errorKey]: null,
      });

      return {
        success: true,
        data: rows,
        error: null,
      };
    } catch (error) {
      console.error("Catalog fetch error:", error);

      setStore({
        [dataKey]: [],
        [loadingKey]: false,
        [errorKey]: errorMessage,
      });
      getActions().showToast(errorMessage, "error");

      return { success: false, data: [], error: errorMessage };
    }
  },
});

export default warehouseCostAnalyticsActions;
